<template>
  <div class="sa-refund-dash">
    <div class="breadcrumb">
      <Breadcrumb>
        <Breadcrumb-item>财务管理</Breadcrumb-item>
        <Breadcrumb-item>班主任退费看板</Breadcrumb-item>
      </Breadcrumb>
    </div>

    <div class="dash-head">
      <div>
        <h1 class="title">班主任退费看板</h1>
        <p class="sub">{{ periodNote || '统计口径加载中…' }}</p>
      </div>
    </div>

    <div class="filterbar">
      <span class="fblabel">时间范围</span>
      <div class="seg">
        <button :class="{on: mode==='days' && days===30}" @click="setDays(30)">近30天</button>
        <button :class="{on: mode==='days' && days===90}" @click="setDays(90)">近90天</button>
        <button :class="{on: mode==='days' && days===180}" @click="setDays(180)">近180天</button>
        <button :class="{on: mode==='all'}" @click="setAll">全部</button>
      </div>
      <Select v-model="month" clearable placeholder="按月份查看…" style="width:140px" @on-change="onMonth">
        <Option v-for="m in monthOptions" :key="m" :value="m">{{ m }}</Option>
      </Select>
      <Date-picker type="date" placeholder="开始" v-model="startDate" style="width:130px" @on-change="onCustomDate"></Date-picker>
      <span class="muted">至</span>
      <Date-picker type="date" placeholder="结束" v-model="endDate" style="width:130px" @on-change="onCustomDate"></Date-picker>
      <a class="clear-btn" @click.prevent="resetRange">清除</a>
      <Select v-model="studyAdviserId" clearable placeholder="全部班主任" style="width:160px" :disabled="!privileged && advisers.length<=1" @on-change="load">
        <Option v-for="a in advisers" :key="a.id" :value="a.id">{{ a.name }}</Option>
      </Select>
      <span class="range-text" v-if="periodLabel">当前：<b>{{ periodLabel }}</b>（{{ periodDays }} 天）</span>
      <Button type="primary" size="small" :loading="loading" @click="load" style="margin-left:auto">刷新</Button>
    </div>

    <Spin fix v-if="loading"></Spin>

    <div class="kpis">
      <div class="kpi" v-for="c in kpiCards" :key="c.lab" :class="c.cls">
        <div class="lab">{{ c.lab }}</div>
        <div class="val">{{ c.val }}<small v-if="c.unit">{{ c.unit }}</small></div>
        <div class="foot">{{ c.foot }}</div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-hd">
        <h2>预警提示 <span class="badge">{{ alerts.length }}</span></h2>
        <span class="hint">规则自动触发，优先级从高到低</span>
      </div>
      <div class="panel-bd">
        <div v-if="!alerts.length" class="empty">当前周期内没有触发预警，团队状态平稳。</div>
        <div class="alerts" v-else>
          <div class="alert" v-for="(a,i) in alerts" :key="i" :class="a.level">
            <div>
              <div class="who">{{ a.teacher }}
                <span class="badge" :class="levelBadge(a.level)">{{ levelLabel(a.level) }}</span>
              </div>
              <div class="txt" v-html="a.text"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="grid2">
      <div class="panel">
        <div class="panel-hd">
          <h2>各班主任退费率</h2>
          <span class="hint">退费率 = 退费人数 ÷ 在册</span>
        </div>
        <div class="panel-bd">
          <div v-if="!rateRows.length" class="empty">暂无数据</div>
          <div class="bar-row" v-for="r in rateRows" :key="'r'+r.studyAdviserId">
            <div class="name" :title="r.teacher">{{ r.teacher }}</div>
            <div class="track">
              <div class="fill" :style="{width: rateWidth(r.rate)+'%', background: r.riskColor || '#3b82f6'}"></div>
              <div class="avgline" :style="{left: avgRateLeft+'%'}"></div>
            </div>
            <div class="num"><b>{{ pct(r.rate) }}</b> <span class="muted">{{ r.refundCount }}人</span></div>
          </div>
          <div class="avglegend">虚线为团队均值 <b>{{ pct(avgRate) }}</b> · 分级按 90 天口径折算</div>
        </div>
      </div>
      <div class="panel">
        <div class="panel-hd">
          <h2>退费原因分布</h2>
        </div>
        <div class="panel-bd">
          <div v-if="!reasonRows.length" class="empty">暂无退费记录</div>
          <div class="bar-row reason" v-for="(r,i) in reasonRows" :key="'c'+r.category">
            <div class="name" :title="r.name">{{ r.name }}</div>
            <div class="track">
              <div class="fill" :style="{width: reasonWidth(r.count)+'%', background: palette[i % palette.length]}"></div>
            </div>
            <div class="num"><b>{{ r.count }}</b> <span class="muted">{{ pct(r.share) }}</span></div>
          </div>
        </div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-hd">
        <h2>近 6 个月退费趋势</h2>
        <span class="hint">按申请时间 · 人数去重</span>
      </div>
      <div class="panel-bd">
        <div class="cols">
          <div class="col" v-for="t in trend" :key="t.month">
            <div class="cval">{{ t.count || '' }}</div>
            <div class="cbar" :style="{height: trendHeight(t.count)+'px'}"></div>
            <div class="clab">{{ t.month.slice(5) }}月</div>
          </div>
        </div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-hd">
        <h2>班主任明细</h2>
      </div>
      <div class="panel-bd scroll">
        <Table :columns="teacherColumns" :data="teacherRows" size="small" :loading="loading"></Table>
      </div>
    </div>

    <div class="panel">
      <div class="panel-hd">
        <h2>退费学生明细</h2>
        <div class="filterbar inline">
          <Input v-model="keyword" placeholder="搜学生姓名/班主任" style="width:180px" clearable @on-enter="load" @on-clear="load"></Input>
          <Select v-model="reasonCategory" clearable placeholder="全部原因" style="width:130px" @on-change="load">
            <Option :value="1">外教原因</Option>
            <Option :value="2">学导原因</Option>
            <Option :value="3">销售原因</Option>
            <Option :value="4">技术原因</Option>
            <Option :value="5">产品原因</Option>
            <Option :value="6">学生客观原因</Option>
          </Select>
          <Select v-model="signType" clearable placeholder="新生+续费" style="width:120px" @on-change="load">
            <Option value="新生">仅新生</Option>
            <Option value="续费">仅续费</Option>
          </Select>
          <Button size="small" @click="load">筛选</Button>
        </div>
      </div>
      <div class="panel-bd scroll">
        <Table :columns="studentColumns" :data="studentRows" size="small" :loading="loading"></Table>
        <div v-if="!loading && !studentRows.length" class="empty" style="padding-top:12px">没有符合条件的退费记录</div>
      </div>
      <div class="foot-note">
        <b>口径说明：</b>退费率 = 周期内退费人数 ÷ 周期内在册学员（曾归属 − 期前退费完结）；金额优先实际退款金额；
        退费状态含已审核/已退费/劝退成功；时间按申请时间；新生/续费按购买渠道。三数（新生、续费、退费）不可直接相除。
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StudyAdviserRefundDashboard',
  data () {
    const months = []
    const now = new Date()
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const m = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0')
      months.push(m)
    }
    return {
      loading: false,
      mode: 'days',
      days: 90,
      month: '',
      startDate: '',
      endDate: '',
      studyAdviserId: null,
      keyword: '',
      reasonCategory: null,
      signType: '',
      monthOptions: months,
      periodNote: '',
      periodLabel: '',
      periodDays: 90,
      privileged: true,
      advisers: [],
      kpi: {},
      alerts: [],
      rateRows: [],
      reasonRows: [],
      trend: [],
      teacherRows: [],
      studentRows: [],
      palette: ['#e5484d', '#f5a524', '#8b5cf6', '#3b82f6', '#eab308', '#30a46c'],
      teacherColumns: [
        { title: '班主任', key: 'teacher', minWidth: 100 },
        { title: '新生', key: 'newCount', width: 70, sortable: true },
        { title: '续费', key: 'renewCount', width: 70, sortable: true },
        { title: '在册', key: 'cohort', width: 70, sortable: true },
        { title: '退费数', key: 'refundCount', width: 80, sortable: true },
        { title: '退费率', key: 'rate', width: 90, sortable: true, render: (h, p) => h('span', this.pct(p.row.rate)) },
        { title: '退费金额', key: 'refundAmount', width: 110, sortable: true, render: (h, p) => h('span', this.money(p.row.refundAmount)) },
        { title: '新生退费', key: 'refundNew', width: 90 },
        { title: '续费退费', key: 'refundRenew', width: 90 },
        { title: '主要原因', key: 'mainReason', minWidth: 100 },
        { title: '风险', key: 'riskLabel', width: 80, render: (h, p) => h('span', { class: 'badge ' + this.riskBadge(p.row.riskKey) }, p.row.riskLabel || '—') }
      ],
      studentColumns: [
        { title: '学生', key: 'student', minWidth: 100 },
        { title: '班主任', key: 'teacher', minWidth: 90 },
        { title: '类型', key: 'signType', width: 70 },
        { title: '报名日期', key: 'enrollDate', width: 110 },
        { title: '退费日期', key: 'refundDate', width: 110 },
        { title: '在册时长(天)', key: 'daysOnRoll', width: 110 },
        { title: '课时进度', key: 'lessonProgress', width: 90 },
        { title: '退费金额', key: 'amount', width: 100, render: (h, p) => h('span', this.money(p.row.amount)) },
        { title: '退费原因', key: 'reason', minWidth: 100 },
        { title: '关注点', key: 'focus', width: 90 }
      ]
    }
  },
  computed: {
    kpiCards () {
      const k = this.kpi || {}
      return [
        { lab: '新生总数', val: k.newCount || 0, unit: '人', foot: '周期内新签类（按付款日）' },
        { lab: '续费总数', val: k.renewCount || 0, unit: '人', foot: '周期内续费类' },
        { lab: '退费总数', val: k.refundCount || 0, unit: '人', foot: `当期签当期退 ${k.refundInPeriod || 0} · 往期学员 ${k.refundFromOld || 0}`, cls: 'warn' },
        { lab: '整体退费率', val: ((k.rate || 0) * 100).toFixed(1), unit: '%', foot: `在册 ${k.cohort || 0} 人 · 师均 ${(k.avgRefundPerTeacher || 0).toFixed(1)} 人退费`, cls: 'warn' },
        { lab: '退费金额', val: this.money(k.refundAmount), unit: '', foot: '实际退款优先' },
        { lab: '需介入班主任', val: k.needIntervene || 0, unit: '人', foot: '高风险 + 关注', cls: k.needIntervene ? 'warn' : '' }
      ]
    },
    avgRate () {
      if (!this.rateRows.length) return 0
      const s = this.rateRows.reduce((a, r) => a + (r.rate || 0), 0)
      return s / this.rateRows.length
    },
    avgRateLeft () {
      const max = Math.max(...this.rateRows.map(r => r.rate || 0), 0.05)
      const scale = max * 1.18
      return Math.min(100, (this.avgRate / scale) * 100)
    },
    maxReason () {
      return Math.max(...this.reasonRows.map(r => r.count || 0), 1)
    },
    maxTrend () {
      return Math.max(...this.trend.map(t => t.count || 0), 1)
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    pct (v) { return ((v || 0) * 100).toFixed(1) + '%' },
    money (v) {
      const n = Number(v || 0)
      if (n >= 10000) return (n / 10000).toFixed(1) + '万'
      return n.toLocaleString('zh-CN')
    },
    rateWidth (rate) {
      const max = Math.max(...this.rateRows.map(r => r.rate || 0), 0.05)
      return Math.min(100, ((rate || 0) / (max * 1.18)) * 100)
    },
    reasonWidth (c) { return (c / this.maxReason) * 100 },
    trendHeight (c) { return Math.max(4, (c / this.maxTrend) * 120) },
    levelLabel (lv) { return ({ high: '高风险', mid: '关注', low: '提示' })[lv] || lv },
    levelBadge (lv) { return ({ high: 'b-red', mid: 'b-org', low: 'b-yel' })[lv] || 'b-gry' },
    riskBadge (k) { return ({ high: 'b-red', mid: 'b-org', low: 'b-yel', ok: 'b-grn' })[k] || 'b-gry' },
    setDays (d) {
      this.mode = 'days'; this.days = d; this.month = ''; this.startDate = ''; this.endDate = ''; this.load()
    },
    setAll () {
      this.mode = 'all'; this.month = ''; this.startDate = ''; this.endDate = ''; this.load()
    },
    onMonth (v) {
      if (!v) return
      this.mode = 'month'; this.startDate = ''; this.endDate = ''; this.load()
    },
    onCustomDate () {
      if (!this.startDate && !this.endDate) return
      this.mode = 'range'; this.month = ''; this.load()
    },
    resetRange () {
      this.mode = 'days'; this.days = 90; this.month = ''; this.startDate = ''; this.endDate = ''; this.load()
    },
    fmtDate (v) {
      if (!v) return ''
      if (typeof v === 'string') return v.substring(0, 10)
      const d = new Date(v)
      if (isNaN(d.getTime())) return ''
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
    },
    load () {
      const self = this
      self.loading = true
      const params = {
        studyAdviserId: self.studyAdviserId || undefined,
        keyword: self.keyword || undefined,
        reasonCategory: self.reasonCategory || undefined,
        signType: self.signType || undefined
      }
      if (self.mode === 'all') {
        params.allTime = true
      } else if (self.mode === 'month' && self.month) {
        params.month = self.month
      } else if (self.mode === 'range') {
        params.startDate = self.fmtDate(self.startDate) || undefined
        params.endDate = self.fmtDate(self.endDate) || undefined
      } else {
        params.days = self.days
      }
      self.$http.get('/financial/studyAdviserRefund/dashboard', { params: params }).then(function (res) {
        self.loading = false
        if (!res || res.status !== 'ok') {
          self.$Message.error((res && res.msg) || '看板加载失败')
          return
        }
        const d = res.data || {}
        self.kpi = d.kpi || {}
        self.alerts = d.alerts || []
        self.rateRows = d.rateRows || []
        self.reasonRows = d.reasonRows || []
        self.trend = d.trend || []
        self.teacherRows = d.teacherRows || []
        self.studentRows = d.studentRows || []
        self.periodNote = d.periodNote || ''
        self.periodLabel = d.periodLabel || ''
        self.periodDays = d.periodDays || 0
        self.privileged = !!d.privileged
        self.advisers = d.advisers || []
      }).catch(function () {
        self.loading = false
        self.$Message.error('看板加载失败')
      })
    }
  }
}
</script>

<style scoped lang="less">
.sa-refund-dash {
  --bg: #f4f5f7; --card: #fff; --line: #e8eaed; --t1: #111827; --t2: #4b5563; --t3: #9ca3af;
  --red: #e5484d; --orange: #f5a524; --yellow: #eab308; --green: #30a46c;
  position: relative; padding-bottom: 40px; color: var(--t1);
}
.title { font-size: 22px; font-weight: 650; margin: 8px 0 0; }
.sub { color: var(--t3); font-size: 12.5px; margin: 4px 0 12px; }
.filterbar {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  background: #fff; border: 1px solid var(--line); border-radius: 12px;
  padding: 10px 14px; margin-bottom: 14px;
}
.filterbar.inline { border: 0; padding: 0; background: transparent; margin: 0; }
.fblabel { font-size: 12.5px; color: var(--t3); font-weight: 600; }
.seg { display: flex; background: #eceef1; border-radius: 9px; padding: 3px; gap: 2px; }
.seg button {
  border: 0; background: transparent; padding: 6px 13px; border-radius: 7px;
  font-size: 13px; color: var(--t2); cursor: pointer;
}
.seg button.on { background: #fff; color: var(--t1); font-weight: 600; box-shadow: 0 1px 2px rgba(16,24,40,.1); }
.clear-btn { color: var(--t3); font-size: 12px; }
.range-text { font-size: 12.5px; color: var(--t2); }
.muted { color: var(--t3); font-size: 12px; }
.kpis { display: grid; grid-template-columns: repeat(6, 1fr); gap: 12px; margin-bottom: 14px; }
.kpi {
  background: var(--card); border: 1px solid var(--line); border-radius: 12px; padding: 14px 16px;
}
.kpi.warn { border-color: #f7d9a8; background: linear-gradient(180deg,#fffdf8,#fff); }
.kpi .lab { font-size: 12px; color: var(--t3); margin-bottom: 7px; }
.kpi .val { font-size: 26px; font-weight: 660; line-height: 1.1; }
.kpi .val small { font-size: 13px; font-weight: 500; color: var(--t2); margin-left: 2px; }
.kpi .foot { font-size: 11.5px; color: var(--t3); margin-top: 6px; }
.panel {
  background: var(--card); border: 1px solid var(--line); border-radius: 12px; margin-bottom: 14px;
}
.panel-hd {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  padding: 14px 18px; border-bottom: 1px solid var(--line);
}
.panel-hd h2 { font-size: 14.5px; font-weight: 620; margin: 0; display: flex; align-items: center; gap: 8px; }
.panel-hd .hint { font-size: 12px; color: var(--t3); }
.panel-bd { padding: 16px 18px; }
.badge {
  font-size: 11px; font-weight: 600; padding: 2px 7px; border-radius: 5px; background: #f1f3f5; color: #5c6470;
}
.b-red { background: #fdecec; color: #c52a2f; }
.b-org { background: #fdf3e3; color: #b56a06; }
.b-yel { background: #fdf8e3; color: #8a6b05; }
.b-grn { background: #e9f7ef; color: #1d7a4d; }
.b-gry { background: #f1f3f5; color: #5c6470; }
.alerts { display: grid; grid-template-columns: repeat(auto-fill, minmax(330px, 1fr)); gap: 10px; }
.alert {
  border: 1px solid var(--line); border-radius: 10px; padding: 11px 13px; border-left: 3px solid var(--t3);
}
.alert.high { border-left-color: var(--red); background: #fffafa; }
.alert.mid { border-left-color: var(--orange); background: #fffdf8; }
.alert.low { border-left-color: var(--yellow); }
.alert .who { font-weight: 620; font-size: 13px; margin-bottom: 2px; }
.alert .txt { font-size: 12.5px; color: var(--t2); line-height: 1.5; }
.empty { color: var(--t3); font-size: 13px; }
.grid2 { display: grid; grid-template-columns: 1.15fr 1fr; gap: 14px; margin-bottom: 14px; }
.bar-row { display: grid; grid-template-columns: 64px 1fr 96px; align-items: center; gap: 10px; margin-bottom: 9px; }
.bar-row.reason { grid-template-columns: 112px 1fr 82px; }
.bar-row .name { font-size: 12.5px; color: var(--t2); text-align: right; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.track { position: relative; height: 16px; background: #f4f5f7; border-radius: 4px; overflow: hidden; }
.fill { height: 100%; border-radius: 4px; }
.avgline { position: absolute; top: -4px; bottom: -4px; width: 1px; border-left: 1.5px dashed #9ca3af; }
.bar-row .num { font-size: 12px; color: var(--t2); text-align: right; }
.avglegend { font-size: 11.5px; color: var(--t3); margin-top: 10px; padding-top: 10px; border-top: 1px dashed var(--line); }
.cols { display: flex; align-items: flex-end; gap: 10px; height: 150px; padding-top: 18px; }
.col { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; gap: 6px; }
.col .cbar { width: 100%; max-width: 52px; background: linear-gradient(180deg, #c7d7fb, #9dbcf7); border-radius: 4px 4px 0 0; }
.col .cval { font-size: 11.5px; color: var(--t2); }
.col .clab { font-size: 11.5px; color: var(--t3); }
.scroll { max-height: 520px; overflow: auto; }
.foot-note { padding: 14px 18px; border-top: 1px solid var(--line); font-size: 12px; color: var(--t3); line-height: 1.7; }
@media (max-width: 1180px) {
  .kpis { grid-template-columns: repeat(3, 1fr); }
  .grid2 { grid-template-columns: 1fr; }
}
</style>



// WEBPACK FOOTER //
// StudyAdviserRefundDashboard.vue?d5b411d0